// What the console's OBS Control tab runs in OBS on top of the scene buttons,
// over obsControl's connection (src/lib/obsControl.js), for a package's
// screens as the OBS export builds them:
//
//   - The booth follows the console. VALORANT's Commentator page draws two
//     windows, over the Up Next screen and the bracket, or one larger window
//     for the camera alone (the Commentators tab's "Camera only"), and the
//     export puts those two overlays' browser sources and the camera under
//     them (obsExport.js, COMMENTATOR_WINDOW_OVERLAYS, COMMENTATOR_SOURCES).
//     A change of layout is set for a server time a few seconds ahead
//     (display.commentator.switchAt): the page slides its windows at that
//     instant, and this module has OBS slide the camera and the Bracket with
//     them, showing and hiding the three for the layout — one request batch
//     of transforms and sleeps that OBS plays on its own clock, sent a second
//     before, so the console's tab being in the background changes nothing.
//     The windows' places come from the page's layout settings (the public
//     layout endpoint), so OBS's sources sit under the windows as Package
//     Art has them; on connect they are put there at once. Marvel Rivals'
//     page keeps a camera per window (two or one, by the switch and the
//     names), which OBS shows and hides as before.
//   - Countdown cuts. Once Starting Soon's or Be Right Back's countdown has
//     run out on screen, OBS cuts to the scene the operator picked, if it is
//     still on that screen (an operator who has moved on is left alone).
//   - Side swap. In-Match has two sources, the page and its ?flip=1 twin with
//     the players' sides swapped, one shown at a time: the console flips which.
//   - The mic. A package with a known microphone (MIC in obsExport.js:
//     VALORANT's HyperX SoloCast 2) gets OBS's global mic put on that device
//     on connect, picked by name, since the export can't name the device.
//
// Like the OBS connection, the settings are this machine's (localStorage per
// package, beside the scene map): they belong to the PC that runs OBS, and an
// operator's console elsewhere never drives it. Commands never throw; what
// happens goes in obsControl's activity log.

import { obsControl } from './obsControl.js';
import {
  CAMERA_COMMENTATOR_PACKAGES,
  COMMENTATOR_CAMERAS,
  BOOTH_COMMENTATOR_PACKAGES,
  COMMENTATOR_SOURCES,
  IN_MATCH_SLUG,
  IN_MATCH_SWAPPED_SUFFIX,
  commentatorWindowSources,
  commentatorWindows,
  layoutSourceName,
  micOf,
} from './obsExport.js';

const settingsKey = (packageSlug) => `streamgg.obs.stage.${packageSlug}`;

const COMMENTATOR_SLUG = 'commentator';
const COUNTDOWNS = {
  starting: { from: 'starting-soon', label: 'Starting Soon' },
  brb: { from: 'be-right-back', label: 'Be Right Back' },
};

// The page's 00:00 comes on its first state poll after the stored end (every
// 2 s), and its timer then flickers out (~0.9 s): the cut waits for both, so
// the viewer sees the countdown finish.
export const CUT_AFTER_MS = 3000;
// A countdown that ended longer ago than this when the console learnt of it
// is an old value still stored, not a cut to make now.
const STALE_MS = 10000;
// The page draws a new camera layout on its first state poll after the
// console's autosave (1.2 s, then up to 2 s), so OBS's cameras follow a
// change about then and the windows never show the wrong camera for long.
const CAMERA_FOLLOW_MS = 3000;
// The booth's slide: its length and easing (valorant/commentator.html
// has the same two, SLIDE_MS and ease: they must match), its frame rate in
// OBS, and how long before the switch the batch is sent — enough for a
// background tab's timer, which Chrome lets run up to a second late, to be
// in time, and short enough that a second click within the switch's lead
// cancels it rather than sending another. A switch this long past when the
// console learns of it is put in place without a slide, as the page does.
const SLIDE_MS = 700;
const SLIDE_FPS = 30;
const SLIDE_SEND_LEAD_MS = 1000;
const SLIDE_LATE_MS = 1500;
const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
// How long the page's layout settings (the windows' places) are kept.
const WINDOWS_TTL_MS = 15000;

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled — the settings just won't persist */
  }
};

function defaultSettings(slugs) {
  const has = (slug) => slugs.includes(slug);
  return {
    camerasFollow: true,
    cuts: {
      // After Starting Soon a stream introduces its first match: Up Next when
      // the package has it (VALORANT), else the commentators.
      starting: { on: false, to: has('up-next') ? 'up-next' : has(COMMENTATOR_SLUG) ? COMMENTATOR_SLUG : IN_MATCH_SLUG },
      brb: { on: false, to: IN_MATCH_SLUG },
    },
  };
}

class ObsStage {
  constructor() {
    this.packageSlug = null;
    this.overlays = [];
    this.settings = defaultSettings([]);
    this.booth = false; // the package's page is the booth of two windows (VALORANT)
    this.layout = null; // 'pair' | 'one': the windows the page draws now
    this.switchAt = null; // when the layout changes, server ms (booth)
    this.namedCount = 0; // commentators with a name (one name = one window)
    this.applied = null; // the layout last put on OBS's cameras
    this._slideTimer = null;
    this._windows = null; // { at, boxes }: the windows' places, kept a while
    this.swapped = null; // In-Match: is the twin the one shown (null = unknown)
    this.serverOffset = 0;
    this.countdowns = { starting: null, brb: null }; // endsAt, server time
    this.nextCut = { starting: null, brb: null }; // { at: local ms, to }
    this.fired = { starting: null, brb: null }; // the endsAt each last cut for
    this._cutTimers = { starting: null, brb: null };
    this._cameraTimer = null;
    this._listeners = new Set();
    this._snap = null;
    this._rebuildSnapshot();

    this.subscribe = this.subscribe.bind(this);
    this.getSnapshot = this.getSnapshot.bind(this);

    obsControl.onConnected(() => this._onConnected());
    obsControl.onObsEvent((type, data) => this._onObsEvent(type, data));
  }

  // ── subscription (useSyncExternalStore contract) ─────────────────────────

  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  getSnapshot() {
    return this._snap;
  }

  _rebuildSnapshot() {
    this._snap = {
      settings: this.settings,
      layout: this.layout,
      applied: this.applied,
      swapped: this.swapped,
      nextCut: { ...this.nextCut },
      // Each countdown's end on this machine's clock, for the console to show.
      countdowns: {
        starting: this.countdowns.starting ? this.countdowns.starting - this.serverOffset : null,
        brb: this.countdowns.brb ? this.countdowns.brb - this.serverOffset : null,
      },
      namedCount: this.namedCount,
      booth: this.booth,
      camerasAvailable: this.camerasAvailable,
      swapAvailable: this.swapAvailable,
      cutsAvailable: {
        starting: this._has(COUNTDOWNS.starting.from),
        brb: this._has(COUNTDOWNS.brb.from),
      },
    };
  }

  _emit() {
    this._rebuildSnapshot();
    for (const listener of this._listeners) listener();
  }

  _has(slug) {
    return this.overlays.some((o) => o.slug === slug);
  }

  get camerasAvailable() {
    return CAMERA_COMMENTATOR_PACKAGES.has(this.packageSlug) && this._has(COMMENTATOR_SLUG);
  }

  get swapAvailable() {
    return this._has(IN_MATCH_SLUG);
  }

  // ── from the console (PackageProduction, on every relevant state change) ──

  configure({ packageSlug, overlays, commentators, shared, switchAt = null, startingEndsAt, brbEndsAt, serverOffset }) {
    if (packageSlug !== this.packageSlug) {
      this.packageSlug = packageSlug;
      this.overlays = overlays ?? [];
      this.booth = BOOTH_COMMENTATOR_PACKAGES.has(packageSlug);
      const defaults = defaultSettings(this.overlays.map((o) => o.slug));
      const stored = readJson(settingsKey(packageSlug)) ?? {};
      this.settings = {
        camerasFollow: stored.camerasFollow ?? defaults.camerasFollow,
        cuts: {
          starting: { ...defaults.cuts.starting, ...stored.cuts?.starting },
          brb: { ...defaults.cuts.brb, ...stored.cuts?.brb },
        },
      };
      // Another package's console: its timers and what it put on OBS are not
      // this one's.
      clearTimeout(this._cameraTimer);
      this._cameraTimer = null;
      clearTimeout(this._slideTimer);
      this._slideTimer = null;
      this._windows = null;
      this.switchAt = null;
      for (const which of Object.keys(COUNTDOWNS)) {
        clearTimeout(this._cutTimers[which]);
        this._cutTimers[which] = null;
        this.nextCut[which] = null;
      }
      this.layout = null;
      this.applied = null;
      this.swapped = null;
      this.fired = { starting: null, brb: null };
      this.countdowns = { starting: null, brb: null };
      if (obsControl.isConnected) {
        this.readSwap();
        this.pickMic();
      }
    } else {
      this.overlays = overlays ?? this.overlays;
    }
    this.serverOffset = Number(serverOffset) || 0;

    // The pages' own rules ("booth" in each commentator.html): VALORANT's
    // shows its pair of windows (Up Next and the bracket) unless Camera only;
    // Marvel Rivals' two windows unless the camera is shared or only one
    // commentator is named.
    const named = ['left', 'right'].filter((side) => String(commentators?.[side]?.name ?? '').trim());
    this.namedCount = named.length;
    const layout = this.booth ? (shared ? 'one' : 'pair') : (!shared && named.length !== 1 ? 'pair' : 'one');
    const at = Number(switchAt) || null;
    if (layout !== this.layout) {
      const first = this.layout === null;
      this.layout = layout;
      this.switchAt = at;
      if (this.booth) this._scheduleSlide(first ? null : at);
      else this._scheduleCameras(first ? 0 : CAMERA_FOLLOW_MS);
    } else if (this.booth && at !== this.switchAt) {
      // The same layout with a new time: a switch undone within its lead.
      // The page plays nothing for it, and nor does OBS.
      this.switchAt = at;
      clearTimeout(this._slideTimer);
      this._slideTimer = null;
    }

    this._setCountdown('starting', startingEndsAt);
    this._setCountdown('brb', brbEndsAt);
    this._emit();
  }

  // ── settings ─────────────────────────────────────────────────────────────

  _saveSettings(next) {
    this.settings = next;
    if (this.packageSlug) writeJson(settingsKey(this.packageSlug), next);
    this._emit();
  }

  setCamerasFollow(on) {
    this._saveSettings({ ...this.settings, camerasFollow: on === true });
    if (!on) return;
    if (this.booth) this._scheduleSlide(null);
    else this._scheduleCameras(0);
  }

  setCut(which, patch) {
    const cuts = { ...this.settings.cuts, [which]: { ...this.settings.cuts[which], ...patch } };
    this._saveSettings({ ...this.settings, cuts });
    this._scheduleCut(which);
    this._emit();
  }

  // ── cameras follow the console ───────────────────────────────────────────

  _scheduleCameras(delay) {
    clearTimeout(this._cameraTimer);
    this._cameraTimer = null;
    if (!this.camerasAvailable || !this.settings.camerasFollow) return;
    this._cameraTimer = setTimeout(() => this.applyCameras(), delay);
  }

  async applyCameras() {
    this._cameraTimer = null;
    if (!this.camerasAvailable || !this.settings.camerasFollow || !this.layout || !obsControl.isConnected) return false;
    const scene = obsControl.sceneMap[COMMENTATOR_SLUG];
    const pair = this.layout === 'pair';
    const show = pair ? [COMMENTATOR_CAMERAS.left, COMMENTATOR_CAMERAS.right] : [COMMENTATOR_CAMERAS.one];
    const hide = pair ? [COMMENTATOR_CAMERAS.one] : [COMMENTATOR_CAMERAS.left, COMMENTATOR_CAMERAS.right];
    // The new window's cameras come on before the old ones go, so no window
    // is ever left over OBS's empty canvas.
    let ok = true;
    for (const name of show) ok = (await obsControl.setSceneItemEnabled(scene, name, true)) && ok;
    for (const name of hide) ok = (await obsControl.setSceneItemEnabled(scene, name, false)) && ok;
    if (ok) {
      this.applied = this.layout;
      obsControl.note(true, pair
        ? 'cameras: two windows — Left and Right Commentator Camera shown'
        : 'cameras: one window — One Camera for Both shown');
    }
    this._emit();
    return ok;
  }

  // ── the booth follows the console (VALORANT) ────────────────────────────

  // The sources' move for the layout: a slide at the console's time, sent to
  // OBS SLIDE_SEND_LEAD_MS before it; with no time (on connect, the switch
  // turned on) they are put in place at once.
  _scheduleSlide(switchAt) {
    clearTimeout(this._slideTimer);
    this._slideTimer = null;
    if (!this.camerasAvailable || !this.settings.camerasFollow) return;
    const at = switchAt ? switchAt - this.serverOffset : null;
    const delay = at ? at - SLIDE_SEND_LEAD_MS - Date.now() : 0;
    this._slideTimer = setTimeout(() => this.applyBooth(at), Math.max(0, delay));
  }

  // The windows' places, from the page's layout settings as the public
  // layout endpoint serves them, kept WINDOWS_TTL_MS; the design's when it
  // can't be read.
  async _windowsNow() {
    const now = Date.now();
    if (this._windows && now - this._windows.at < WINDOWS_TTL_MS) return this._windows.boxes;
    let vars = {};
    try {
      const resp = await fetch(`/api/art/layouts/${this.packageSlug}/commentator?v=2`, { credentials: 'same-origin' });
      if (resp.ok) vars = (await resp.json())?.vars ?? {};
    } catch {
      /* the design's places */
    }
    this._windows = { at: now, boxes: commentatorWindows(vars) };
    return this._windows.boxes;
  }

  // Puts OBS's sources under the page's windows for the layout: in the pair,
  // the Up Next and Bracket overlays' browser sources under the two windows
  // and the camera hidden; alone, the camera under the one window and the
  // two overlays hidden. As a slide ending where the page's does, starting
  // at `at` (this machine's ms) when that is set and not long past, else at
  // once. One request batch: a Sleep to the start, then a transform per
  // moving source per frame with a Sleep between frames, which OBS plays on
  // its own clock. The camera travels with the left window: it takes the
  // window as the slide to the one window starts, and leaves it, hidden,
  // once the slide back has ended, where Up Next shows again. The Bracket
  // travels with the right window: shown before it slides in, hidden once
  // it has slid out. Up Next never moves; it shows and hides.
  async applyBooth(at = null) {
    this._slideTimer = null;
    if (!this.camerasAvailable || !this.settings.camerasFollow || !this.layout || !obsControl.isConnected) return false;
    const scene = obsControl.sceneMap[COMMENTATOR_SLUG];
    const pair = this.layout === 'pair';
    const windows = commentatorWindowSources(this.overlays);
    if (!windows.left || !windows.right) {
      obsControl.note(false, 'the Up Next and Bracket overlays are not both granted: nothing to put under the windows');
      this._emit();
      return false;
    }
    const CAMERA = 0, LEFT = 1, RIGHT = 2;
    const names = [COMMENTATOR_SOURCES.camera, windows.left, windows.right];
    const [ids, boxes, transforms] = await Promise.all([
      Promise.all(names.map((name) => obsControl.sceneItemId(scene, name))),
      this._windowsNow(),
      Promise.all(names.map((name) => obsControl.sceneItemTransform(scene, name))),
    ]);
    const missing = names.filter((name, i) => ids[i] == null);
    if (missing.length) {
      obsControl.note(false, `"${missing.join('" and "')}" ${missing.length > 1 ? "aren't" : "isn't"} in the scene "${scene}"`);
      this._emit();
      return false;
    }
    const to = pair
      ? [boxes.camera.pair, boxes.camera.pair, boxes.game.pair]
      : [boxes.camera.one, boxes.camera.pair, boxes.game.out];
    const from = transforms.map((t, i) => (t
      ? { x: t.positionX, y: t.positionY, w: t.boundsWidth || to[i].w, h: t.boundsHeight || to[i].h }
      : to[i]));
    const moving = [CAMERA, RIGHT];
    const lead = at ? at - Date.now() : null;
    const moves = moving.some((i) => ['x', 'y', 'w', 'h'].some((k) => from[i][k] !== to[i][k]));
    const slide = lead !== null && lead > -SLIDE_LATE_MS && moves;
    const transform = (i, b) => ({
      requestType: 'SetSceneItemTransform',
      requestData: {
        sceneName: scene,
        sceneItemId: ids[i],
        sceneItemTransform: { positionX: b.x, positionY: b.y, boundsType: 'OBS_BOUNDS_SCALE_OUTER', boundsWidth: b.w, boundsHeight: b.h },
      },
    });
    const enabled = (i, on) => ({ requestType: 'SetSceneItemEnabled', requestData: { sceneName: scene, sceneItemId: ids[i], sceneItemEnabled: on } });
    const sleep = (ms) => ({ requestType: 'Sleep', requestData: { sleepMillis: Math.round(ms) } });
    // Up Next sits in the left window wherever Package Art has it; the
    // Bracket is shown off the screen before it slides in; the camera takes
    // the left window as the slide to the one window starts.
    const requests = [transform(LEFT, to[LEFT])];
    if (pair) requests.push(enabled(RIGHT, true));
    if (slide && lead > 0) requests.push(sleep(lead));
    if (!pair) requests.push(enabled(LEFT, false), enabled(CAMERA, true));
    if (slide) {
      const frames = Math.round((SLIDE_MS / 1000) * SLIDE_FPS);
      for (let f = 1; f <= frames; f += 1) {
        const e = ease(f / frames);
        moving.forEach((i) => {
          const a = from[i], b = to[i];
          requests.push(transform(i, {
            x: a.x + (b.x - a.x) * e,
            y: a.y + (b.y - a.y) * e,
            w: a.w + (b.w - a.w) * e,
            h: a.h + (b.h - a.h) * e,
          }));
        });
        if (f < frames) requests.push(sleep(1000 / SLIDE_FPS));
      }
    } else {
      moving.forEach((i) => requests.push(transform(i, to[i])));
    }
    if (pair) requests.push(enabled(CAMERA, false), enabled(LEFT, true));
    else requests.push(enabled(RIGHT, false));
    const ok = await obsControl.batch(
      pair ? 'booth: Up Next and the bracket in the windows' : 'booth: the camera alone',
      requests,
      { timeoutMs: Math.max(0, lead ?? 0) + SLIDE_MS + 8000 },
    );
    if (ok) {
      this.applied = this.layout;
      obsControl.note(true, pair
        ? `booth: Up Next and the bracket in the windows${slide ? ', slid in' : ''} — ${names[LEFT]} and ${names[RIGHT]} under them, ${names[CAMERA]} off`
        : `booth: the camera alone${slide ? ', slid' : ''} — ${names[CAMERA]} under the window, ${names[LEFT]} off and ${names[RIGHT]} off the screen`);
    }
    this._emit();
    return ok;
  }

  // ── countdown cuts ───────────────────────────────────────────────────────

  _setCountdown(which, endsAt) {
    const value = Number(endsAt) || null;
    if (value === this.countdowns[which]) return;
    this.countdowns[which] = value;
    this._scheduleCut(which);
  }

  _scheduleCut(which) {
    clearTimeout(this._cutTimers[which]);
    this._cutTimers[which] = null;
    this.nextCut[which] = null;
    const endsAt = this.countdowns[which];
    const cut = this.settings.cuts[which];
    if (!endsAt || !cut?.on || !this._has(COUNTDOWNS[which].from) || this.fired[which] === endsAt) return;
    const at = endsAt - this.serverOffset + CUT_AFTER_MS;
    const delay = at - Date.now();
    if (delay < -STALE_MS) return;
    this.nextCut[which] = { at, to: cut.to };
    this._cutTimers[which] = setTimeout(() => this._cut(which, endsAt), Math.max(0, delay));
  }

  async _cut(which, endsAt) {
    this._cutTimers[which] = null;
    this.nextCut[which] = null;
    if (this.fired[which] === endsAt) return;
    this.fired[which] = endsAt;
    const { from, label } = COUNTDOWNS[which];
    const to = this.settings.cuts[which].to;
    const fromScene = obsControl.sceneMap[from];
    const toName = this.overlays.find((o) => o.slug === to)?.name ?? to;
    if (!obsControl.isConnected) {
      obsControl.note(false, `${label} countdown ended — OBS isn't connected, no cut to ${toName}`);
    } else if (obsControl.currentProgramScene !== fromScene) {
      obsControl.note(true, `${label} countdown ended — OBS is on "${obsControl.currentProgramScene}", left alone`);
    } else {
      obsControl.note(true, `${label} countdown ended → ${toName}`);
      await obsControl.setScene(to);
    }
    this._emit();
  }

  // ── In-Match side swap ───────────────────────────────────────────────────

  _swapSources() {
    const inMatch = this.overlays.find((o) => o.slug === IN_MATCH_SLUG);
    if (!inMatch) return null;
    return {
      scene: obsControl.sceneMap[IN_MATCH_SLUG],
      normal: layoutSourceName(inMatch.name),
      twin: layoutSourceName(`${inMatch.name}${IN_MATCH_SWAPPED_SUFFIX}`),
    };
  }

  async readSwap() {
    const src = this._swapSources();
    this.swapped = src && obsControl.isConnected ? await obsControl.getSceneItemEnabled(src.scene, src.twin) : null;
    this._emit();
    return this.swapped;
  }

  // `to`: the sides wanted (true = swapped); left out, the other way round
  // from now.
  async swapSides(to = this.swapped !== true) {
    const src = this._swapSources();
    if (!src || !obsControl.isConnected) return false;
    const toSwapped = to === true;
    const [show, hide] = toSwapped ? [src.twin, src.normal] : [src.normal, src.twin];
    // The incoming source comes on before the other goes, so the banners
    // never blink out.
    const ok = (await obsControl.setSceneItemEnabled(src.scene, show, true))
      && (await obsControl.setSceneItemEnabled(src.scene, hide, false));
    if (ok) obsControl.note(true, `In-Match sides: ${toSwapped ? 'swapped' : 'as start.gg has them'}`);
    await this.readSwap();
    return ok;
  }

  // ── OBS side ─────────────────────────────────────────────────────────────

  _onConnected() {
    // A freshly opened OBS (or one restarted) has whatever its collection
    // saved: put the cameras where the page's windows are, the mic on its
    // device, and read the sides.
    this.applied = null;
    if (this.booth) this._scheduleSlide(null);
    else this._scheduleCameras(0);
    this.pickMic();
    this.readSwap();
  }

  // The package's mic (MIC in obsExport.js) on OBS's global mic input, by
  // device name (obsControl.pickMicDevice). Null for a package with none.
  async pickMic() {
    const mic = micOf(this.packageSlug);
    if (!mic || !obsControl.isConnected) return null;
    return obsControl.pickMicDevice(mic);
  }

  _onObsEvent(type, data) {
    if (type === 'SceneItemEnableStateChanged') {
      // Sides changed in OBS itself (a hotkey, a click on the eye): follow.
      const src = this._swapSources();
      if (src && data.sceneName === src.scene) this.readSwap();
    } else if (type === 'SceneListChanged' || type === 'SceneItemCreated' || type === 'SceneItemRemoved') {
      this.readSwap();
    }
  }
}

export const obsStage = new ObsStage();
